/**
 * React Hook for RTDB-cached fixtures with real-time updates
 */

import { useState, useEffect, useCallback } from 'react';
import rtdbFootballService from '../rtdbFootballService';
import rtdbCache from '../rtdbCache';
import { CACHE_PATHS, getCacheKey, logCache } from '../rtdbConfig';

/**
 * Hook for fetching fixtures with RTDB caching and real-time updates
 * @param {Object} params - Query parameters
 * @param {string} params.date - Date in YYYY-MM-DD format
 * @param {string} params.league - League ID
 * @param {string} params.country - Country name
 * @param {boolean} params.enabled - Whether to fetch data
 * @param {boolean} params.realtime - Enable real-time updates
 * @returns {Object} Fixtures data, loading state, error, and refetch function
 */
export const useRTDBFixtures = ({ 
  date, 
  league, 
  country, 
  season,
  enabled = true,
  realtime = true 
}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cacheStatus, setCacheStatus] = useState('idle');

  const fetchFixtures = useCallback(async () => {
    if (!enabled) return;

    try {
      setLoading(true);
      setError(null);
      setCacheStatus('fetching');

      logCache('info', 'useRTDBFixtures', { date, league, country, season });

      console.log('🔍 [useRTDBFixtures] Fetching with params:', {
        date,
        league: league ? Number(league) : null,
        season,
        country
      });
      console.log('🔍 [useRTDBFixtures] Date param specifically:', date, 'Type:', typeof date);
      
      // Use RTDB-enhanced service with Eastern Time for consistency
      const timezone = 'America/New_York'; // Always use ET for all users

      const response = await rtdbFootballService.getFixtures(
        null, null, null, date, league ? Number(league) : null,
        season || null, null, null, null, null, null, null, null, null, timezone
      );

      console.log('🌍 [useRTDBFixtures] Using timezone: Eastern Time (ET)');
      
      let fixtures = response.response || [];
      
      // Filter by country if needed (when no specific league)
      if (country && !league) {
        fixtures = fixtures.filter(fixture => 
          fixture.league?.country === country ||
          fixture.league?.country?.name === country
        );
      }
      
      setData(fixtures);
      setCacheStatus('success');
    } catch (err) {
      setError(err.message || 'Failed to load fixtures');
      setCacheStatus('error');
      console.error('Failed to load fixtures:', err);
    } finally {
      setLoading(false);
    }
  }, [date, league, country, season, enabled]);

  // Set up real-time subscription
  useEffect(() => {
    if (!enabled || !realtime || !date) return;

    let unsubscribe = () => {};
    
    // Build cache path — must match the key used by rtdbFootballService.getFixtures
    let cachePath;
    if (league) {
      const seasonSuffix = season ? `_${season}` : '';
      cachePath = `${CACHE_PATHS.FIXTURES}/${date}/${getCacheKey('league', league)}${seasonSuffix}`;
    } else {
      cachePath = `${CACHE_PATHS.FIXTURES}/${date}/all`;
    }

    // Subscribe to real-time updates
    unsubscribe = rtdbCache.subscribe(cachePath, (cachedData) => {
      if (cachedData) {
        logCache('info', 'Real-time update received', { path: cachePath });
        
        let fixtures = cachedData.response || [];
        
        // Apply country filter if needed
        if (country && !league) {
          fixtures = fixtures.filter(fixture => 
            fixture.league?.country === country ||
            fixture.league?.country?.name === country
          );
        }
        
        setData(fixtures);
        setCacheStatus('realtime-update');
      } else {
        // Cache expired or missing, trigger refresh
        fetchFixtures();
      }
    });

    // Initial fetch
    fetchFixtures();

    return () => {
      unsubscribe();
    };
  }, [date, league, country, season, enabled, realtime]); // Don't include fetchFixtures to avoid loops

  return { 
    data, 
    loading, 
    error, 
    refetch: fetchFixtures,
    cacheStatus,
    isConnected: rtdbCache.isConnected()
  };
};

/**
 * Hook for live fixtures with real-time updates
 */
export const useRTDBLiveFixtures = ({ enabled = true }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!enabled) return;

    let unsubscribe = () => {};
    const cachePath = `${CACHE_PATHS.FIXTURES}/live`;

    const fetchLive = async () => {
      setLoading(true);
      try {
        const response = await rtdbFootballService.getFixtures(
          null, null, 'all', null, null, null, null, null, null, 
          null, null, null, null, null, 'UTC'
        );
        setData(response.response || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    // Subscribe to live updates
    unsubscribe = rtdbCache.subscribe(cachePath, (cachedData) => {
      if (cachedData) {
        setData(cachedData.response || []);
      } else {
        fetchLive();
      }
    });

    // Initial fetch
    fetchLive();

    // Refresh live data more frequently
    const interval = setInterval(fetchLive, 30000); // Every 30 seconds

    return () => {
      unsubscribe();
      clearInterval(interval);
    };
  }, [enabled]);

  return { data, loading, error };
};